/**
 * Quick View Modal
 * 
 * Advanced modal system for quick project previews:
 * - Smooth slide-in animations
 * - Backdrop blur effects
 * - Gesture-driven dismissal
 * - Touch-friendly mobile interactions
 * - Image gallery preview
 * - Key information display
 */

'use client';

import React, { useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';

export interface QuickViewModalProps {
  project: PropertyDevelopmentProject | null;
  isOpen: boolean;
  onClose: () => void;
  messages: {
    close: string;
    viewFullDetails: string;
    availableUnits: string;
    totalUnits: string;
    soldUnits: string;
    starting: string;
    completion: string;
    projectType: string;
    enquireNow: string;
  };
  accentColor?: string;
  locale?: string;
}

/**
 * Quick View Modal Component
 */
export function QuickViewModal({
  project,
  isOpen,
  onClose,
  messages,
  accentColor = '#BBA339',
  locale = 'en',
}: QuickViewModalProps) {
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!project) return null;

  const availabilityPercentage = project.totalUnits && project.soldUnits
    ? ((project.totalUnits - project.soldUnits) / project.totalUnits) * 100
    : 0;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'TBA';
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
          />

          {/* Modal Container */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              className="relative w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl"
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 50 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={0.2}
              onDragEnd={(_, info) => {
                if (info.offset.y > 100) {
                  onClose();
                }
              }}
            >
              {/* Close Button */}
              <motion.button
                onClick={onClose}
                className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-md transition-colors hover:bg-black/70"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="sr-only">{messages.close}</span>
              </motion.button>

              {/* Drag Indicator */}
              <div className="flex justify-center pt-2">
                <div className="h-1 w-12 rounded-full bg-gray-300" />
              </div>

              <div className="grid max-h-[90vh] grid-cols-1 overflow-y-auto lg:grid-cols-2">
                {/* Left: Image Section */}
                <div className="relative h-96 lg:h-auto">
                  {project.heroImageUrl ? (
                    <Image
                      src={project.heroImageUrl}
                      alt={project.name}
                      fill
                      className="object-cover"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                  ) : (
                    <div
                      className="h-full w-full bg-gradient-to-br"
                      style={{
                        background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}80 100%)`,
                      }}
                    />
                  )}

                  {/* Overlay Badges */}
                  <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-end justify-between gap-2">
                    {/* Project Type */}
                    {project.projectType && (
                      <motion.span
                        className="rounded-lg bg-white/90 px-3 py-1.5 text-xs font-bold uppercase tracking-wider backdrop-blur-md"
                        style={{ color: accentColor }}
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        {project.projectType.replace(/_/g, ' ')}
                      </motion.span>
                    )}

                    {/* Status Badge */}
                    <motion.span
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-md ${
                        project.status === 'SOLD_OUT'
                          ? 'bg-red-600'
                          : project.status === 'UPCOMING'
                          ? 'bg-blue-600'
                          : 'bg-green-600'
                      }`}
                      initial={{ x: 20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      {project.status?.replace(/_/g, ' ')}
                    </motion.span>
                  </div>
                </div>

                {/* Right: Content Section */}
                <div className="flex flex-col p-8">
                  {/* Project Name */}
                  <motion.h2
                    className="mb-3 text-3xl font-bold text-gray-900"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    {project.name}
                  </motion.h2>

                  {/* Price */}
                  {project.displayPriceText && (
                    <motion.div
                      className="mb-6"
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      <div className="text-sm font-medium text-gray-500">
                        {messages.starting}
                      </div>
                      <div className="text-3xl font-bold" style={{ color: accentColor }}>
                        {project.displayPriceText}
                      </div>
                    </motion.div>
                  )}

                  {/* Description */}
                  <motion.p
                    className="mb-6 text-gray-600"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    {project.description}
                  </motion.p>

                  {/* Stats Grid */}
                  <motion.div
                    className="mb-6 grid grid-cols-3 gap-4"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    {/* Total Units */}
                    <div className="rounded-xl bg-gray-50 p-4 text-center">
                      <div className="text-2xl font-bold" style={{ color: accentColor }}>
                        {project.totalUnits || 0}
                      </div>
                      <div className="text-xs font-medium text-gray-500">
                        {messages.totalUnits}
                      </div>
                    </div>

                    {/* Available Units */}
                    <div className="rounded-xl bg-gray-50 p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {project.availableUnits || 0}
                      </div>
                      <div className="text-xs font-medium text-gray-500">
                        {messages.availableUnits}
                      </div>
                    </div>

                    {/* Sold Units */}
                    <div className="rounded-xl bg-gray-50 p-4 text-center">
                      <div className="text-2xl font-bold text-gray-400">
                        {project.soldUnits || 0}
                      </div>
                      <div className="text-xs font-medium text-gray-500">
                        {messages.soldUnits}
                      </div>
                    </div>
                  </motion.div>

                  {/* Progress Bar */}
                  {project.totalUnits && (
                    <motion.div
                      className="mb-6"
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      <div className="mb-2 flex justify-between text-sm font-medium text-gray-600">
                        <span>Availability</span>
                        <span>{Math.round(availabilityPercentage)}%</span>
                      </div>
                      <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ backgroundColor: accentColor }}
                          initial={{ width: 0 }}
                          animate={{ width: `${availabilityPercentage}%` }}
                          transition={{ duration: 1, delay: 0.6 }}
                        />
                      </div>
                    </motion.div>
                  )}

                  {/* Additional Info */}
                  <motion.div
                    className="mb-6 space-y-3"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    {project.unitMixSummary && (
                      <div className="flex items-center gap-3">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        <span className="text-sm text-gray-700">{project.unitMixSummary}</span>
                      </div>
                    )}

                    {project.estimatedCompletionDate && (
                      <div className="flex items-center gap-3">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-gray-700">
                          {messages.completion}: {formatDate(project.estimatedCompletionDate)}
                        </span>
                      </div>
                    )}
                  </motion.div>

                  {/* Actions */}
                  <motion.div
                    className="mt-auto flex gap-3"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.7 }}
                  >
                    <Link
                      href={`/${locale}/projects/${project.slug || project.id}`}
                      className="flex-1 rounded-xl py-3 text-center text-sm font-semibold text-white transition-all hover:shadow-lg"
                      style={{ backgroundColor: accentColor }}
                      onClick={onClose}
                    >
                      {messages.viewFullDetails}
                    </Link>

                    <motion.button
                      className="rounded-xl border-2 px-6 py-3 text-sm font-semibold transition-all hover:bg-gray-50"
                      style={{ borderColor: accentColor, color: accentColor }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {messages.enquireNow}
                    </motion.button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

